import React, { useState, useEffect, useMemo } from 'react';
import { 
  Plus, Trash2, Edit3, Save, X, Eye, Clock, Heart, 
  TrendingUp, CheckCircle, XCircle, AlertCircle, 
  Filter, Search, Star, Play, ExternalLink, Copy,
  BarChart3, Users, Flame, Zap, Laugh, Frown,
  RefreshCw, Loader, ChevronDown, ChevronUp, GripVertical
} from 'lucide-react';

// Import real API services
import { adminPeakMomentsAPI } from '../services/api';

// Style constants to match admin theme
const styles = {
  card: {
    backgroundColor: '#1A1A1A',
    border: '1px solid #262626',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2)'
  },
  compactCard: {
    backgroundColor: '#1A1A1A',
    border: '1px solid #262626',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px'
  },
  button: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s'
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #404040',
    borderRadius: '6px',
    backgroundColor: '#262626',
    color: '#F5F5F5',
    fontSize: '14px',
    outline: 'none'
  },
  textarea: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #404040',
    borderRadius: '6px',
    backgroundColor: '#262626',
    color: '#F5F5F5',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    minHeight: '80px'
  },
  badge: {
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase'
  },
  primaryText: '#F5F5F5',
  secondaryText: '#A3A3A3'
};

const CATEGORIES = {
  'movies': { label: 'Movies', color: '#8B5CF6' },
  'tv-shows': { label: 'TV Shows', color: '#10B981' },
  'anime': { label: 'Anime', color: '#EF4444' },
  'music-performance': { label: 'Music', color: '#F59E0B' },
  'korean-drama': { label: 'K-Drama', color: '#EC4899' },
  'general': { label: 'General', color: '#6B7280' }
};

const MOMENT_TYPES = {
  'plot_twist': { label: 'Plot Twist', emoji: '🤯', color: '#F59E0B' },
  'emotional': { label: 'Emotional', emoji: '😭', color: '#EC4899' },
  'funny': { label: 'Funny', emoji: '😂', color: '#10B981' },
  'shocking': { label: 'Shocking', emoji: '😱', color: '#EF4444' },
  'epic': { label: 'Epic', emoji: '🔥', color: '#F97316' },
  'cringe': { label: 'Cringe', emoji: '😬', color: '#6B7280' },
  'wholesome': { label: 'Wholesome', emoji: '🥰', color: '#4ADE80' },
  'dramatic': { label: 'Dramatic', emoji: '🎭', color: '#7C3AED' },
  'scary': { label: 'Scary', emoji: '👻', color: '#374151' },
  'exciting': { label: 'Exciting', emoji: '⚡', color: '#FACC15' },
  'nostalgic': { label: 'Nostalgic', emoji: '💭', color: '#4B5563' },
  'satisfying': { label: 'Satisfying', emoji: '✅', color: '#16A34A' },
  'unexpected': { label: 'Unexpected', emoji: '🤨', color: '#DB2777' },
  'viral': { label: 'Viral', emoji: '📈', color: '#DC2626' }
};

const STATUS_COLORS = {
  'pending': { bg: '#F59E0B20', color: '#F59E0B', label: 'Pending' },
  'approved': { bg: '#10B98120', color: '#10B981', label: 'Approved' },
  'rejected': { bg: '#EF444420', color: '#EF4444', label: 'Rejected' },
  'flagged': { bg: '#EC489920', color: '#EC4899', label: 'Flagged' }
};

// Loading spinner
const LoadingSpinner = ({ size = 20 }) => (
  <Loader style={{ width: size, height: size, animation: 'spin 1s linear infinite' }} />
);

// Alert component
const Alert = ({ type, children, onClose }) => {
  const colors = {
    success: { bg: '#10B98110', border: '#10B981', text: '#10B981' },
    error: { bg: '#EF444410', border: '#EF4444', text: '#EF4444' },
    info: { bg: '#3B82F610', border: '#3B82F6', text: '#3B82F6' }
  };
  const style = colors[type] || colors.info;

  return (
    <div style={{
      padding: '12px 16px',
      backgroundColor: style.bg,
      border: `1px solid ${style.border}`,
      borderRadius: '8px',
      color: style.text,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px'
    }}>
      <span>{children}</span>
      {onClose && (
        <button onClick={onClose} style={{ 
          background: 'none', border: 'none', color: style.text, cursor: 'pointer', padding: '4px' 
        }}>
          <X size={16} />
        </button>
      )}
    </div>
  );
};

// Button component
const Button = ({ children, onClick, variant = 'primary', size = 'md', icon: Icon, loading, disabled, title }) => {
  const variants = {
    primary: { bg: '#8B5CF6', color: '#fff', hover: '#7C3AED' },
    secondary: { bg: '#262626', color: '#E5E5E5', hover: '#404040' },
    success: { bg: '#10B981', color: '#fff', hover: '#059669' },
    error: { bg: '#EF4444', color: '#fff', hover: '#DC2626' },
    warning: { bg: '#F59E0B', color: '#fff', hover: '#D97706' },
    outline: { bg: 'transparent', color: '#8B5CF6', hover: '#8B5CF610', border: '#8B5CF6' }
  };
  
  const sizes = { sm: '6px 12px', md: '8px 16px', lg: '12px 20px' };
  const variantStyle = variants[variant];

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      title={title}
      style={{
        ...styles.button,
        padding: sizes[size],
        backgroundColor: variantStyle.bg,
        color: variantStyle.color,
        border: variantStyle.border ? `1px solid ${variantStyle.border}` : 'none',
        opacity: disabled || loading ? 0.6 : 1
      }}
      onMouseEnter={(e) => !disabled && !loading && (e.target.style.backgroundColor = variantStyle.hover)}
      onMouseLeave={(e) => !disabled && !loading && (e.target.style.backgroundColor = variantStyle.bg)}
    >
      {loading ? <LoadingSpinner size={14} /> : Icon && <Icon size={14} />}
      {children}
    </button>
  );
};

// Peak Moment Row Component
const PeakMomentRow = ({
  moment, onEdit, onApprove, onReject, onDelete, isSelected, onSelect,
  draggable, isDragging, isDropTarget, onDragStart, onDragOver, onDrop, onDragEnd,
}) => {
  const [expanded, setExpanded] = useState(false);
  const statusConfig = STATUS_COLORS[moment.status] || STATUS_COLORS.pending;
  const momentTypeConfig = MOMENT_TYPES[moment.momentType] || MOMENT_TYPES.exciting;
  const categoryConfig = CATEGORIES[moment.category] || CATEGORIES.general;

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const copyTimestampUrl = () => {
    const url = `https://www.youtube.com/watch?v=${moment.videoId}&t=${moment.timestampSeconds}s`;
    navigator.clipboard.writeText(url);
    // Could show toast notification
  };

  const getYouTubeUrl = () => {
    return `https://www.youtube.com/watch?v=${moment.videoId}&t=${moment.timestampSeconds}s`;
  };

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      style={{
        backgroundColor: isSelected ? '#8B5CF620' : '#262626',
        border: `1px solid ${isDropTarget ? '#8B5CF6' : (isSelected ? '#8B5CF6' : '#404040')}`,
        borderTop: isDropTarget ? '3px solid #8B5CF6' : undefined,
        borderRadius: '8px',
        marginBottom: '8px',
        opacity: isDragging ? 0.4 : 1,
        transition: 'opacity 0.15s, border-color 0.15s'
      }}
    >
      {/* Main Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: draggable ? '22px 40px 1fr auto auto' : '40px 1fr auto auto',
        alignItems: 'center',
        gap: '12px',
        padding: '12px'
      }}>
        {/* Drag handle — only the grip starts a drag, so text stays selectable */}
        {draggable && (
          <div
            title="Drag to reorder"
            style={{
              cursor: 'grab', color: '#737373', display: 'flex',
              alignItems: 'center', justifyContent: 'center'
            }}
          >
            <GripVertical size={17} />
          </div>
        )}

        {/* Checkbox */}
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(moment._id, e.target.checked)}
          style={{
            width: '16px',
            height: '16px',
            accentColor: '#8B5CF6'
          }}
        />

        {/* Main Content */}
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <img
              src={moment.channelAvatarUrl || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="20" height="20"%3E%3Crect fill="%23404040" width="20" height="20"/%3E%3C/svg%3E'}
              alt={moment.channelName}
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                objectFit: 'cover'
              }}
              onError={(e) => {
                e.target.onerror = null; // Prevent infinite loop
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="20" height="20"%3E%3Crect fill="%23404040" width="20" height="20"/%3E%3C/svg%3E';
              }}
            />
            <span style={{ fontSize: '12px', color: styles.secondaryText }}>{moment.channelName}</span>
            
            {moment.featured && (
              <span style={{
                ...styles.badge,
                backgroundColor: '#F59E0B20',
                color: '#F59E0B'
              }}>
                ⭐ Featured
              </span>
            )}

            {moment.isSpoiler && (
              <span style={{
                ...styles.badge,
                backgroundColor: '#EF444420',
                color: '#F87171'
              }}>
                ⚠️ Spoiler
              </span>
            )}
          </div>
          
          <h4 style={{
            fontSize: '14px',
            fontWeight: '600',
            color: styles.primaryText,
            margin: '0 0 4px 0',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            <a
              href={getYouTubeUrl()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                color: 'inherit',
                textDecoration: 'none',
                transition: 'color 0.15s'
              }}
              onMouseEnter={(e) => { e.target.style.color = '#8B5CF6'; e.target.style.textDecoration = 'underline'; }}
              onMouseLeave={(e) => { e.target.style.color = 'inherit'; e.target.style.textDecoration = 'none'; }}
              title={`Open on YouTube at ${moment.timestampFormatted || '0:00'}`}
            >
              {moment.title}
            </a>
          </h4>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{
              ...styles.badge,
              backgroundColor: statusConfig.bg,
              color: statusConfig.color
            }}>
              {statusConfig.label}
            </span>
            
            <span style={{
              ...styles.badge,
              backgroundColor: categoryConfig.color + '20',
              color: categoryConfig.color
            }}>
              {categoryConfig.label}
            </span>
            
            <span style={{
              ...styles.badge,
              backgroundColor: momentTypeConfig.color + '20',
              color: momentTypeConfig.color
            }}>
              {momentTypeConfig.emoji} {momentTypeConfig.label}
            </span>
            
            <span style={{ fontSize: '11px', color: styles.secondaryText }}>
              <Clock size={10} style={{ marginRight: '2px', verticalAlign: 'middle' }} />
              {moment.timestampFormatted}
            </span>
          </div>
        </div>

        {/* Stats */}
        <div style={{ textAlign: 'right', fontSize: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px', color: '#EC4899' }}>
              <Heart size={12} />
              <span>{moment.likes || 0}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px', color: '#3B82F6' }}>
              <Eye size={12} />
              <span>{moment.views || 0}</span>
            </div>
          </div>
          <div style={{ color: styles.secondaryText, marginTop: '4px' }}>
            {formatDate(moment.createdAt)}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <Button
            size="sm"
            variant="secondary"
            icon={expanded ? ChevronUp : ChevronDown}
            onClick={() => setExpanded(!expanded)}
            title="Toggle details"
          />
          
          <Button
            size="sm"
            variant="secondary"
            icon={ExternalLink}
            onClick={() => window.open(getYouTubeUrl(), '_blank')}
            title="Open in YouTube"
          />
          
          <Button
            size="sm"
            variant="secondary"
            icon={Copy}
            onClick={copyTimestampUrl}
            title="Copy timestamp URL"
          />
          
          {moment.status === 'pending' && (
            <>
              <Button
                size="sm"
                variant="success"
                icon={CheckCircle}
                onClick={() => onApprove(moment._id)}
                title="Approve"
              />
              <Button
                size="sm"
                variant="error"
                icon={XCircle}
                onClick={() => onReject(moment._id)}
                title="Reject"
              />
            </>
          )}
          
          <Button
            size="sm"
            variant="outline"
            icon={Edit3}
            onClick={() => onEdit(moment)}
            title="Edit"
          />
          
          <Button
            size="sm"
            variant="error"
            icon={Trash2}
            onClick={() => onDelete(moment._id)}
            title="Delete"
          />
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div style={{
          borderTop: '1px solid #404040',
          padding: '16px 12px',
          backgroundColor: '#1A1A1A'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '16px' }}>
            {/* Thumbnail */}
            <img
              src={moment.videoThumbnail}
              alt={moment.videoTitle}
              style={{
                width: '120px',
                height: '68px',
                objectFit: 'cover',
                borderRadius: '6px'
              }}
              onError={(e) => {
                e.target.onerror = null; // Prevent infinite loop
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="68"%3E%3Crect fill="%23404040" width="120" height="68"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23888" font-size="12"%3ENo Image%3C/text%3E%3C/svg%3E';
              }}
            />
            
            {/* Details */}
            <div>
              <div style={{ marginBottom: '8px' }}>
                <strong style={{ color: styles.primaryText }}>Description:</strong>
                <p style={{ margin: '4px 0', color: '#E5E5E5', fontSize: '13px', lineHeight: '1.4' }}>
                  {moment.description}
                </p>
              </div>
              
              <div style={{ marginBottom: '8px' }}>
                <strong style={{ color: styles.primaryText }}>Video:</strong>
                <p style={{ margin: '4px 0', color: '#E5E5E5', fontSize: '13px' }}>
                  {moment.videoTitle}
                </p>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px', fontSize: '12px' }}>
                <div>
                  <strong style={{ color: styles.secondaryText }}>Video ID:</strong>
                  <span style={{ color: '#E5E5E5', marginLeft: '4px', fontFamily: 'monospace' }}>
                    {moment.videoId}
                  </span>
                </div>
                <div>
                  <strong style={{ color: styles.secondaryText }}>Priority:</strong>
                  <span style={{ color: '#E5E5E5', marginLeft: '4px' }}>
                    {moment.priority || 0}
                  </span>
                </div>
                <div>
                  <strong style={{ color: styles.secondaryText }}>Submitted by:</strong>
                  <span style={{ color: '#E5E5E5', marginLeft: '4px' }}>
                    {moment.submittedBy || 'Unknown'}
                  </span>
                </div>
                {moment.moderatedBy && (
                  <div>
                    <strong style={{ color: styles.secondaryText }}>Moderated by:</strong>
                    <span style={{ color: '#E5E5E5', marginLeft: '4px' }}>
                      {moment.moderatedBy}
                    </span>
                  </div>
                )}
              </div>
              
              {moment.moderationNotes && (
                <div style={{ marginTop: '8px' }}>
                  <strong style={{ color: styles.secondaryText }}>Moderation Notes:</strong>
                  <p style={{ margin: '4px 0', color: '#E5E5E5', fontSize: '12px', fontStyle: 'italic' }}>
                    {moment.moderationNotes}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Clip length bounds — must match the server clamp and the PeakMoment model
const CLIP_MIN = 5;
const CLIP_MAX = 60;
const CLIP_DEFAULT = 15;

const formatClock = (total) => {
  const s = Math.max(0, parseInt(total, 10) || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}`;
};

// Create/Edit Modal
const PeakMomentModal = ({ isOpen, onClose, moment, onSave, isEditing }) => {
  const [formData, setFormData] = useState({
    youtubeUrl: '',
    timestampSeconds: '',
    clipDurationSeconds: CLIP_DEFAULT,
    title: '',
    description: '',
    category: 'general',
    momentType: 'exciting',
    featured: false,
    priority: 0,
    isSpoiler: false
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (moment && isEditing) {
      setFormData({
        youtubeUrl: moment.videoUrl || '',
        timestampSeconds: moment.timestampSeconds || '',
        // Moments created before clip length existed have no value stored
        clipDurationSeconds: moment.clipDurationSeconds || CLIP_DEFAULT,
        title: moment.title || '',
        description: moment.description || '',
        category: moment.category || 'general',
        momentType: moment.momentType || 'exciting',
        featured: moment.featured || false,
        priority: moment.priority || 0,
        isSpoiler: moment.isSpoiler || false
      });
    } else {
      setFormData({
        youtubeUrl: '',
        timestampSeconds: '',
        clipDurationSeconds: CLIP_DEFAULT,
        title: '',
        description: '',
        category: 'general',
        momentType: 'exciting',
        featured: false,
        priority: 0,
        isSpoiler: false
      });
    }
  }, [moment, isEditing, isOpen]);

  // ✅ Auto-extract timestamp from URL
  const handleUrlChange = (url) => {
    setFormData(prev => ({ ...prev, youtubeUrl: url }));
    
    // Extract timestamp if present
    const tMatch = url.match(/[?&]t=(\d+)/);
    if (tMatch) {
      const seconds = parseInt(tMatch[1]);
      setFormData(prev => ({ ...prev, timestampSeconds: seconds }));
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description) {
      alert('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      alert('Failed to save: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#1A1A1A',
        padding: '24px',
        borderRadius: '12px',
        border: '1px solid #262626',
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h3 style={{ color: styles.primaryText, marginBottom: '20px', fontSize: '18px' }}>
          {isEditing ? `Edit Peak Moment: ${moment?.title}` : 'Create New Peak Moment'}
        </h3>

        <div style={{ display: 'grid', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', color: '#E5E5E5', fontSize: '14px' }}>
              YouTube URL *
            </label>
            <input
              type="url"
              value={formData.youtubeUrl}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder="https://youtube.com/watch?v=VIDEO_ID&t=123s"
              style={styles.input}
            />
            <div style={{ fontSize: '12px', color: '#A3A3A3', marginTop: '4px' }}>
              Timestamp will be auto-extracted from URL if present (&t=123s)
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', color: '#E5E5E5', fontSize: '14px' }}>
              Timestamp (seconds) *
            </label>
            <input
              type="number"
              value={formData.timestampSeconds}
              onChange={(e) => setFormData(prev => ({ ...prev, timestampSeconds: e.target.value }))}
              placeholder="e.g., 125 (for 2:05)"
              style={styles.input}
            />
            {formData.timestampSeconds !== '' && (
              <div style={{ fontSize: '12px', color: '#A3A3A3', marginTop: '4px' }}>
                Starts at {formatClock(formData.timestampSeconds)}
              </div>
            )}
          </div>

          {/* Clip length — where the moment stops. The swipe feed plays exactly
              this window, so it needs to be editable here too, not just on submit. */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
              <label style={{ color: '#E5E5E5', fontSize: '14px' }}>
                Clip Length
              </label>
              <span style={{ color: '#8B5CF6', fontSize: '13px', fontWeight: 700, fontFamily: 'monospace' }}>
                {formData.clipDurationSeconds}s
              </span>
            </div>
            <input
              type="range"
              min={CLIP_MIN}
              max={CLIP_MAX}
              step={1}
              value={formData.clipDurationSeconds}
              onChange={(e) => setFormData(prev => ({ ...prev, clipDurationSeconds: parseInt(e.target.value, 10) }))}
              style={{ width: '100%', accentColor: '#8B5CF6', cursor: 'pointer' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '12px', color: '#A3A3A3' }}>
              <span>{CLIP_MIN}s</span>
              <span style={{ fontFamily: 'monospace', color: '#E5E5E5' }}>
                {formData.timestampSeconds !== ''
                  ? `Plays ${formatClock(formData.timestampSeconds)} → ${formatClock(parseInt(formData.timestampSeconds, 10) + formData.clipDurationSeconds)}`
                  : 'Set a timestamp to preview the range'}
              </span>
              <span>{CLIP_MAX}s</span>
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', color: '#E5E5E5', fontSize: '14px' }}>
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Brief description of the moment"
              style={styles.input}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', color: '#E5E5E5', fontSize: '14px' }}>
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Detailed description of what makes this moment special"
              style={styles.textarea}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', color: '#E5E5E5', fontSize: '14px' }}>
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                style={{ ...styles.input, cursor: 'pointer', colorScheme: 'dark' }}
              >
                {Object.entries(CATEGORIES).map(([key, config]) => (
                  <option key={key} value={key} style={{ backgroundColor: '#262626', color: '#F5F5F5' }}>
                    {config.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', color: '#E5E5E5', fontSize: '14px' }}>
                Moment Type
              </label>
              <select
                value={formData.momentType}
                onChange={(e) => setFormData(prev => ({ ...prev, momentType: e.target.value }))}
                style={{ ...styles.input, cursor: 'pointer', colorScheme: 'dark' }}
              >
                {Object.entries(MOMENT_TYPES).map(([key, config]) => (
                  <option key={key} value={key} style={{ backgroundColor: '#262626', color: '#F5F5F5' }}>{config.emoji} {config.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', justifyContent: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#E5E5E5', fontSize: '14px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                  style={{ accentColor: '#8B5CF6' }}
                />
                Featured Moment
              </label>

              {/* Warning tag over the clip in the feed; nothing gets hidden */}
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: formData.isSpoiler ? '#F87171' : '#E5E5E5', fontSize: '14px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.isSpoiler}
                  onChange={(e) => setFormData(prev => ({ ...prev, isSpoiler: e.target.checked }))}
                  style={{ accentColor: '#EF4444' }}
                />
                ⚠️ Spoiler Alert
              </label>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', color: '#E5E5E5', fontSize: '14px' }}>
                Priority (higher shows first)
              </label>
              <input
                type="number"
                min="0"
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                style={styles.input}
              />
            </div>
          </div>

        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving}>
            {isEditing ? 'Update' : 'Create'} Peak Moment
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Admin Peak Moments Tab Component
const AdminPeakMomentsTab = ({ setAlert }) => {
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all', // Show all by default to see everything
    category: 'all',
    momentType: 'all',
    sortBy: 'createdAt'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMoments, setSelectedMoments] = useState(new Set());
  const [showModal, setShowModal] = useState(false);
  const [editingMoment, setEditingMoment] = useState(null);
  const [stats, setStats] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);

  // Load data using real API
  const loadMoments = async (page = 1, append = false) => {
    try {
      if (!append) setLoading(true);

      console.log('🔥 AdminPeakMomentsTab: Loading moments...', { filters, page, limit: 50 });
      const response = await adminPeakMomentsAPI.getAdminPeakMoments({
        ...filters,
        page,
        limit: 50
      });

      console.log('✅ AdminPeakMomentsTab: Received response:', response);
      console.log('📊 Response structure:', {
        hasData: !!response.data,
        dataLength: response.data?.length,
        hasPagination: !!response.pagination,
        fullResponse: response
      });

      if (append) {
        setMoments(prev => [...prev, ...response.data]);
      } else {
        setMoments(response.data);
      }

      setHasNextPage(response.pagination?.hasNextPage || false);
      setCurrentPage(page);

      console.log('✅ AdminPeakMomentsTab: Moments set:', response.data?.length || 0);
    } catch (error) {
      console.error('❌ AdminPeakMomentsTab: Error loading moments:', error);
      setAlert({ type: 'error', message: 'Failed to load peak moments: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await adminPeakMomentsAPI.getAdminPeakMomentsStats();
      setStats(response.stats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  useEffect(() => {
    loadMoments(1, false);
    loadStats();
  }, [filters]);

  // Handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
    setSelectedMoments(new Set());
  };

  const handleSelectMoment = (momentId, selected) => {
    setSelectedMoments(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(momentId);
      } else {
        newSet.delete(momentId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedMoments(new Set(filteredMoments.map(m => m._id)));
    } else {
      setSelectedMoments(new Set());
    }
  };

  const handleCreate = () => {
    setEditingMoment(null);
    setShowModal(true);
  };

  const handleEdit = (moment) => {
    setEditingMoment(moment);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    try {
      if (editingMoment) {
        await adminPeakMomentsAPI.updatePeakMoment(editingMoment._id, formData);
        setAlert({ type: 'success', message: 'Peak moment updated successfully!' });
      } else {
        await adminPeakMomentsAPI.createPeakMoment(formData);
        setAlert({ type: 'success', message: 'Peak moment created successfully!' });
      }
      
      loadMoments(1, false);
      loadStats();
    } catch (error) {
      throw new Error(error.message || 'Failed to save peak moment');
    }
  };

  const handleApprove = async (momentId) => {
    try {
      await adminPeakMomentsAPI.approvePeakMoment(momentId);
      setAlert({ type: 'success', message: 'Peak moment approved!' });
      loadMoments(currentPage, false);
      loadStats();
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to approve: ' + error.message });
    }
  };

  const handleReject = async (momentId) => {
    const reason = prompt('Reason for rejection (optional):');
    try {
      await adminPeakMomentsAPI.rejectPeakMoment(momentId, reason);
      setAlert({ type: 'success', message: 'Peak moment rejected.' });
      loadMoments(currentPage, false);
      loadStats();
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to reject: ' + error.message });
    }
  };

  const handleDelete = async (momentId) => {
    if (!window.confirm('Are you sure you want to delete this peak moment?')) return;
    
    try {
      await adminPeakMomentsAPI.deletePeakMoment(momentId);
      setAlert({ type: 'success', message: 'Peak moment deleted.' });
      loadMoments(currentPage, false);
      loadStats();
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to delete: ' + error.message });
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedMoments.size === 0) {
      setAlert({ type: 'info', message: 'No moments selected.' });
      return;
    }

    const momentIds = Array.from(selectedMoments);
    let updates = {};
    
    if (action === 'reject') {
      const reason = prompt('Reason for rejection (optional):');
      updates.reason = reason;
    } else if (action === 'feature' || action === 'unfeature') {
      if (action === 'feature') {
        const priority = prompt('Priority level (0-10):', '5');
        updates.priority = parseInt(priority) || 5;
      }
    }

    try {
      await adminPeakMomentsAPI.bulkActionPeakMoments(action, momentIds, updates);
      setAlert({ type: 'success', message: `Bulk ${action} completed for ${momentIds.length} moments.` });
      setSelectedMoments(new Set());
      loadMoments(currentPage, false);
      loadStats();
    } catch (error) {
      setAlert({ type: 'error', message: 'Bulk action failed: ' + error.message });
    }
  };

  // Filter moments
  const filteredMoments = useMemo(() => {
    return moments.filter(moment => {
      const matchesSearch = searchTerm === '' || 
        moment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        moment.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        moment.channelName.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesSearch;
    });
  }, [moments, searchTerm]);

  /**
   * Drag-to-reorder.
   *
   * Order is persisted as `priority`, the field every sort already uses, so a
   * drag needs no new concept — the list is just written back top-to-bottom.
   * Only allowed with no search filter active: dragging inside a filtered view
   * would reorder rows against neighbours you can't see.
   */
  const canReorder = searchTerm === '';
  const [dragId, setDragId] = useState(null);
  const [dropId, setDropId] = useState(null);

  const handleDrop = async (targetId) => {
    const sourceId = dragId;
    setDragId(null);
    setDropId(null);
    if (!sourceId || sourceId === targetId) return;

    const ordered = [...moments];
    const from = ordered.findIndex(m => m._id === sourceId);
    const to = ordered.findIndex(m => m._id === targetId);
    if (from < 0 || to < 0) return;

    const [moved] = ordered.splice(from, 1);
    ordered.splice(to, 0, moved);
    setMoments(ordered);   // optimistic; the server just mirrors this list

    try {
      await adminPeakMomentsAPI.reorderPeakMoments(ordered.map(m => m._id));
      setAlert({ type: 'success', message: 'Order saved.' });
    } catch (error) {
      setAlert({ type: 'error', message: 'Could not save the new order.' });
      loadMoments(1, false);
    }
  };

  const pendingCount = stats.byStatus?.pending || 0;
  const approvedCount = stats.byStatus?.approved || 0;
  const totalCount = stats.overview?.total || 0;

  return (
    <div style={{ backgroundColor: '#0F0F0F', color: '#F5F5F5', minHeight: '100vh' }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', color: styles.primaryText }}>
              🔥 Peak Moments Management
            </h2>
            <p style={{ margin: 0, color: styles.secondaryText, fontSize: '14px' }}>
              Manage community-submitted peak moments and highlights
            </p>
          </div>
          
          <Button icon={Plus} onClick={handleCreate}>
            Create Peak Moment
          </Button>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '16px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#8B5CF6' }}>
              {totalCount}
            </div>
            <div style={{ fontSize: '12px', color: styles.secondaryText }}>Total</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#F59E0B' }}>
              {pendingCount}
            </div>
            <div style={{ fontSize: '12px', color: styles.secondaryText }}>Pending</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10B981' }}>
              {approvedCount}
            </div>
            <div style={{ fontSize: '12px', color: styles.secondaryText }}>Approved</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#EF4444' }}>
              {stats.byStatus?.featured || 0}
            </div>
            <div style={{ fontSize: '12px', color: styles.secondaryText }}>Featured</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.card}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
            <Search style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '16px',
              height: '16px',
              color: styles.secondaryText
            }} />
            <input
              type="text"
              placeholder="Search moments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                ...styles.input,
                paddingLeft: '40px'
              }}
            />
          </div>

          {/* Filters */}
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            style={{ ...styles.input, width: '150px' }}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="flagged">Flagged</option>
          </select>

          <select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            style={{ ...styles.input, width: '150px' }}
          >
            <option value="all">All Categories</option>
            {Object.entries(CATEGORIES).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>

          <select
            value={filters.sortBy}
            onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            style={{ ...styles.input, width: '150px' }}
          >
            <option value="createdAt">Newest</option>
            <option value="priority">Priority</option>
            <option value="popular">Most Liked</option>
            <option value="pending">Pending First</option>
          </select>
        </div>

        {/* Bulk Actions */}
        {selectedMoments.size > 0 && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#8B5CF620',
            border: '1px solid #8B5CF6',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ color: styles.primaryText }}>
              {selectedMoments.size} moment(s) selected
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button size="sm" variant="success" onClick={() => handleBulkAction('approve')}>
                Approve
              </Button>
              <Button size="sm" variant="error" onClick={() => handleBulkAction('reject')}>
                Reject
              </Button>
              <Button size="sm" variant="warning" onClick={() => handleBulkAction('feature')}>
                Feature
              </Button>
              <Button size="sm" variant="secondary" onClick={() => handleBulkAction('unfeature')}>
                Unfeature
              </Button>
              <Button size="sm" variant="error" onClick={() => handleBulkAction('delete')}>
                Delete
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Moments List */}
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '18px' }}>
            Peak Moments ({filteredMoments.length})
          </h3>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#E5E5E5', fontSize: '14px' }}>
              <input
                type="checkbox"
                checked={selectedMoments.size === filteredMoments.length && filteredMoments.length > 0}
                onChange={(e) => handleSelectAll(e.target.checked)}
                style={{ accentColor: '#8B5CF6' }}
              />
              Select All
            </label>
            
            <Button size="sm" variant="secondary" icon={RefreshCw} onClick={() => loadMoments(1, false)}>
              Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <LoadingSpinner size={32} />
            <p style={{ color: styles.secondaryText, marginTop: '16px' }}>Loading peak moments...</p>
          </div>
        ) : filteredMoments.length > 0 ? (
          <>
            {canReorder ? (
              <div style={{ fontSize: '12.5px', color: styles.secondaryText, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <GripVertical size={14} />
                Drag a row by its handle to change the order moments appear in.
              </div>
            ) : (
              <div style={{ fontSize: '12.5px', color: '#A3A3A3', marginBottom: '10px' }}>
                Clear the search to reorder moments.
              </div>
            )}

            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {filteredMoments.map(moment => (
                <PeakMomentRow
                  key={moment._id}
                  moment={moment}
                  isSelected={selectedMoments.has(moment._id)}
                  onSelect={handleSelectMoment}
                  onEdit={handleEdit}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onDelete={handleDelete}
                  draggable={canReorder}
                  isDragging={dragId === moment._id}
                  isDropTarget={dropId === moment._id && dragId !== moment._id}
                  onDragStart={(e) => {
                    setDragId(moment._id);
                    e.dataTransfer.effectAllowed = 'move';
                    // Firefox refuses to start a drag without payload
                    try { e.dataTransfer.setData('text/plain', moment._id); } catch { /* ignore */ }
                  }}
                  onDragOver={(e) => {
                    if (!dragId) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (dropId !== moment._id) setDropId(moment._id);
                  }}
                  onDrop={(e) => { e.preventDefault(); handleDrop(moment._id); }}
                  onDragEnd={() => { setDragId(null); setDropId(null); }}
                />
              ))}
            </div>

            {hasNextPage && (
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <Button onClick={() => loadMoments(currentPage + 1, true)}>
                  Load More
                </Button>
              </div>
            )}
          </>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: styles.secondaryText
          }}>
            <Flame size={48} style={{ color: '#F97316', opacity: 0.5, marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', marginBottom: '8px', color: styles.primaryText }}>
              No peak moments found
            </h3>
            <p style={{ fontSize: '14px' }}>
              {searchTerm ? 'Try adjusting your search or filters' : 'Create the first peak moment!'}
            </p>
          </div>
        )}
      </div>

      {/* Modal */}
      <PeakMomentModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        moment={editingMoment}
        onSave={handleSave}
        isEditing={!!editingMoment}
      />
    </div>
  );
};

export default AdminPeakMomentsTab;